import { useEffect, useRef } from 'react'

const KEYWORDS = new Set([
  'function',
  'return',
  'const',
  'let',
  'var',
  'for',
  'while',
  'if',
  'else',
  'break',
  'yield',
  'do',
])

const TOKEN_RE =
  /(\/\/.*$)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(\b\d+(?:\.\d+)?\b)|(\b[a-zA-Z_$][\w$]*\b)/g

const TOKEN_CLASS = {
  comment: 'text-slate-500 italic',
  string: 'text-emerald-300',
  number: 'text-amber-300',
  keyword: 'text-fuchsia-400',
  identifier: 'text-sky-300',
  plain: 'text-slate-200',
}

function tokenize(line) {
  const tokens = []
  let last = 0
  TOKEN_RE.lastIndex = 0

  let match
  while ((match = TOKEN_RE.exec(line)) !== null) {
    if (match.index > last) {
      tokens.push(['plain', line.slice(last, match.index)])
    }

    let type
    if (match[1]) type = 'comment'
    else if (match[2]) type = 'string'
    else if (match[3]) type = 'number'
    else type = KEYWORDS.has(match[4]) ? 'keyword' : 'identifier'

    tokens.push([type, match[0]])
    last = match.index + match[0].length
  }

  if (last < line.length) {
    tokens.push(['plain', line.slice(last)])
  }

  return tokens
}

export default function CodeViewer({ title = 'Code', code, currentLine }) {
  const scrollRef = useRef(null)
  const currentRef = useRef(null)

  useEffect(() => {
    const scrollEl = scrollRef.current
    const lineEl = currentRef.current
    if (!scrollEl || !lineEl) return

    const scrollTop = scrollEl.getBoundingClientRect().top
    const lineTop = lineEl.getBoundingClientRect().top - scrollTop + scrollEl.scrollTop
    const lineHeight = lineEl.offsetHeight
    const viewTop = scrollEl.scrollTop
    const viewBottom = viewTop + scrollEl.clientHeight

    if (lineTop < viewTop || lineTop + lineHeight > viewBottom) {
      scrollEl.scrollTo({ top: lineTop - scrollEl.clientHeight / 2, behavior: 'auto' })
    }
  }, [currentLine])

  if (!code) return null

  const lines = code.split('\n')

  return (
    <div className="flex flex-col h-[60vh] w-full bg-slate-900 rounded-lg border border-slate-700 overflow-hidden text-sm font-mono">
      <div className="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-700 bg-slate-800 shrink-0">
        {title}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-1">
        {lines.map((line, index) => {
          const lineNumber = index + 1
          const active = lineNumber === currentLine

          return (
            <div
              key={lineNumber}
              ref={active ? currentRef : undefined}
              className={`flex ${
                active
                  ? 'bg-yellow-400/15 border-l-2 border-yellow-400'
                  : 'border-l-2 border-transparent'
              }`}
            >
              <span className="w-10 shrink-0 text-right pr-3 text-slate-500 select-none">
                {lineNumber}
              </span>
              <span className="w-5 shrink-0 text-yellow-400 select-none">{active ? '▶' : ''}</span>
              <span className="whitespace-pre flex-1 pr-4">
                {tokenize(line).map(([type, text], tokenIndex) => (
                  <span key={tokenIndex} className={TOKEN_CLASS[type]}>
                    {text}
                  </span>
                ))}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
